import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarClock, ChevronRight, GalleryHorizontal, Megaphone, Pause, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { LOCALES, LOCALE_NAMES, type Locale } from '@app/shared'

import { createAnnouncement, deleteAnnouncement, getAnnouncements, updateAnnouncement, type Announcement, type AnnouncementInput } from '../api/admin'
import { PageHeader, PageSection } from '../components/layout'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  SegmentedControl,
  Select,
  Skeleton,
  Spacer,
  Switch,
  Textarea,
  Toggle,
  cx,
  describeError,
  useToast,
  when,
  type Tone,
} from '../components/ui/Ui'
import styles from './Announcements.module.css'

const EMPTY: AnnouncementInput = { kind: 'ticker', locale: 'ru', title: null, body: '', ctaLabel: null, ctaTo: null, art: null, sortOrder: 0, isEnabled: true, startsAt: null, endsAt: null }

type Art = 'signals' | 'markets' | 'deposit'

/** The artwork the app draws for a banner: `copy` is the signals slot since the pivot; none means none. */
function artOf(art: AnnouncementInput['art']): Art | null {
  if (art === 'copy') return 'signals'
  return art === 'markets' || art === 'deposit' ? art : null
}

/** Where an item stands for a user right now: on air, waiting for its window, past it, or switched off. */
function stateOf(row: Pick<Announcement, 'isEnabled' | 'startsAt' | 'endsAt' | 'kind' | 'title' | 'ctaTo'>, now = Date.now()): { label: string; tone: Tone; live: boolean } {
  if (!row.isEnabled) return { label: 'выключено', tone: 'neutral', live: false }
  if (row.kind === 'banner' && !(row.title && row.ctaTo)) return { label: 'не показывается', tone: 'warning', live: false }
  if (row.endsAt && Date.parse(row.endsAt) <= now) return { label: 'окно прошло', tone: 'neutral', live: false }
  if (row.startsAt && Date.parse(row.startsAt) > now) return { label: 'запланировано', tone: 'info', live: false }
  return { label: 'в эфире', tone: 'accent', live: true }
}

function windowText(row: Pick<Announcement, 'startsAt' | 'endsAt'>): string {
  if (!row.startsAt && !row.endsAt) return 'всегда'
  if (row.startsAt && !row.endsAt) return `с ${when(row.startsAt)}`
  if (!row.startsAt && row.endsAt) return `до ${when(row.endsAt)}`
  return `${when(row.startsAt)} → ${when(row.endsAt)}`
}

/** The home ticker and the banner slides, as the operator writes them. */
export default function Announcements(): JSX.Element {
  const [editing, setEditing] = useState<Announcement | 'new' | null>(null)
  const [form, setForm] = useState<AnnouncementInput>(EMPTY)
  const [locale, setLocale] = useState<Locale | 'all'>('all')
  const queryClient = useQueryClient()
  const toast = useToast()
  const list = useQuery({ queryKey: ['announcements'], queryFn: getAnnouncements })

  const refresh = (): void => void queryClient.invalidateQueries({ queryKey: ['announcements'] })
  const save = useMutation({
    mutationFn: () => (editing === 'new' || editing === null ? createAnnouncement(form) : updateAnnouncement(editing.id, form)),
    onSuccess: () => {
      toast('Сохранено; на главной обновится в течение минуты')
      setEditing(null)
      refresh()
    },
    onError: (error) => toast(describeError(error), true),
  })
  const toggle = useMutation({
    mutationFn: ({ id, isEnabled }: { id: string; isEnabled: boolean }) => updateAnnouncement(id, { isEnabled }),
    onSuccess: refresh,
    onError: (error) => toast(describeError(error), true),
  })
  const remove = useMutation({
    mutationFn: (id: string) => deleteAnnouncement(id),
    onSuccess: () => {
      toast('Удалено')
      setEditing(null)
      refresh()
    },
    onError: (error) => toast(describeError(error), true),
  })

  const openEditor = (row: Announcement | 'new'): void => {
    setEditing(row)
    setForm(
      row === 'new'
        ? EMPTY
        : { kind: row.kind, locale: row.locale, title: row.title, body: row.body, ctaLabel: row.ctaLabel, ctaTo: row.ctaTo, art: row.art, sortOrder: row.sortOrder, isEnabled: row.isEnabled, startsAt: row.startsAt, endsAt: row.endsAt },
    )
  }
  const set = <K extends keyof AnnouncementInput>(key: K, value: AnnouncementInput[K]): void => setForm((current) => ({ ...current, [key]: value }))
  const rows = list.data ?? []

  const locales = LOCALES.filter((code) => rows.some((row) => row.locale === code))
  const shown = rows.filter((row) => locale === 'all' || row.locale === locale).sort((a, b) => a.sortOrder - b.sortOrder || a.createdAt.localeCompare(b.createdAt))
  const banners = shown.filter((row) => row.kind === 'banner')
  const tickers = shown.filter((row) => row.kind === 'ticker')
  const live = rows.filter((row) => stateOf(row).live).length
  const pendingToggle = (id: string): boolean => toggle.isPending && toggle.variables?.id === id

  const addButton = (
    <Button variant="primary" icon={<Plus />} onClick={() => openEditor('new')}>
      Новое объявление
    </Button>
  )

  return (
    <>
      <PageHeader
        title="Объявления"
        description="Бегущая строка и баннеры на главной приложения, по языкам."
        meta={
          list.data && rows.length > 0 ? (
            <>
              <Badge tone={live > 0 ? 'accent' : 'neutral'} pulse={live > 0}>
                в эфире: {live}
              </Badge>
              <Badge tone="neutral" dot={false}>
                всего {rows.length}
              </Badge>
            </>
          ) : null
        }
        actions={addButton}
      />

      {list.isError ? (
        <Card>
          <EmptyState tone="error" icon={<Megaphone />} title="Объявления не загрузились" description={describeError(list.error)} action={<Button onClick={() => void list.refetch()}>Повторить</Button>} />
        </Card>
      ) : list.isPending ? (
        <div className={styles.bannerGrid} aria-busy="true">
          {[0, 1, 2].map((index) => (
            <div key={index} className={styles.item}>
              <div className={styles.stage}>
                <Skeleton height={148} radius={16} />
              </div>
              <div className={styles.itemFoot}>
                <Skeleton width={120} height={12} />
              </div>
            </div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Megaphone />}
            title="Объявлений нет — показываются встроенные"
            description="Добавьте строку для бегущей ленты или баннер для карусели на главной. Каждое объявление пишется для одного языка."
            action={addButton}
          />
        </Card>
      ) : (
        <div className={styles.sections}>
          {locales.length > 1 ? (
            <SegmentedControl<Locale | 'all'>
              value={locale}
              onChange={setLocale}
              ariaLabel="Язык"
              options={[{ value: 'all', label: 'Все языки', count: rows.length }, ...locales.map((code) => ({ value: code, label: LOCALE_NAMES[code], count: rows.filter((row) => row.locale === code).length }))]}
            />
          ) : null}

          <PageSection
            title="Баннеры"
            description="Карточки карусели на главной, по порядку. Без заголовка и ссылки баннер не показывается."
            actions={<span className={styles.count}>{banners.length}</span>}
          >
            {banners.length === 0 ? (
              <Card>
                <EmptyState compact icon={<GalleryHorizontal />} title="Баннеров нет" description="Карусель показывает встроенные карточки." />
              </Card>
            ) : (
              <div className={cx(styles.bannerGrid, 'stagger')}>
                {banners.map((row) => (
                  <article key={row.id} className={cx(styles.item, row.isEnabled ? '' : styles.itemOff)}>
                    <div className={styles.stage}>
                      <BannerPreview title={row.title} body={row.body} cta={row.ctaLabel} art={row.art} />
                    </div>
                    <ItemFoot row={row} busy={pendingToggle(row.id)} onToggle={(next) => toggle.mutate({ id: row.id, isEnabled: next })} onEdit={() => openEditor(row)} />
                  </article>
                ))}
              </div>
            )}
          </PageSection>

          <PageSection
            title="Бегущая строка"
            description="Строки идут одна за другой по порядку; в приложении лента прокручивается сама."
            actions={<span className={styles.count}>{tickers.length}</span>}
          >
            {tickers.length === 0 ? (
              <Card>
                <EmptyState compact icon={<Megaphone />} title="Строк нет" description="Лента показывает встроенные тексты." />
              </Card>
            ) : (
              <div className={cx(styles.tickerList, 'stagger')}>
                {tickers.map((row) => (
                  <article key={row.id} className={cx(styles.item, styles.tickerItem, row.isEnabled ? '' : styles.itemOff)}>
                    <div className={styles.tickerStage}>
                      <TickerPreview text={row.body} />
                    </div>
                    <ItemFoot row={row} busy={pendingToggle(row.id)} onToggle={(next) => toggle.mutate({ id: row.id, isEnabled: next })} onEdit={() => openEditor(row)} />
                  </article>
                ))}
              </div>
            )}
          </PageSection>
        </div>
      )}

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Новое объявление' : 'Объявление'}
        subtitle={editing !== null && editing !== 'new' ? `Создано ${when(editing.createdAt)} · изменено ${when(editing.updatedAt)}` : 'Появится на главной в течение минуты после сохранения.'}
        icon={form.kind === 'banner' ? <GalleryHorizontal /> : <Megaphone />}
        size="lg"
        footer={
          <>
            {editing !== null && editing !== 'new' ? (
              <Button variant="danger" icon={<Trash2 />} loading={remove.isPending} onClick={() => remove.mutate(editing.id)}>
                Удалить
              </Button>
            ) : null}
            <Spacer />
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Отмена
            </Button>
            <Button variant="primary" loading={save.isPending} disabled={form.body.trim() === '' || (form.kind === 'banner' && !(form.title && form.ctaTo))} onClick={() => save.mutate()}>
              Сохранить
            </Button>
          </>
        }
      >
        <div className={styles.editor}>
          <div className={styles.form}>
            <div className={styles.formRow}>
              <span className={styles.formLabel} id="announcement-kind">
                Тип
              </span>
              <SegmentedControl<AnnouncementInput['kind']>
                block
                value={form.kind}
                onChange={(next) => set('kind', next)}
                ariaLabel="Тип объявления"
                options={[
                  { value: 'ticker', label: 'Бегущая строка', icon: <Megaphone /> },
                  { value: 'banner', label: 'Баннер', icon: <GalleryHorizontal /> },
                ]}
              />
            </div>
            <div className={styles.grid2}>
              <Field label="Язык">
                <Select value={form.locale} onChange={(event) => set('locale', event.target.value as AnnouncementInput['locale'])}>
                  {LOCALES.map((code) => (
                    <option key={code} value={code}>
                      {LOCALE_NAMES[code]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Порядок" hint="Меньше — раньше.">
                <Input mono type="number" min={0} value={form.sortOrder} onChange={(event) => set('sortOrder', Number(event.target.value))} />
              </Field>
            </div>
            {form.kind === 'banner' ? (
              <>
                <div className={styles.grid2}>
                  <Field label="Заголовок" error={form.title || form.body.trim() === '' ? undefined : 'Без заголовка баннер не показывается.'}>
                    <Input value={form.title ?? ''} onChange={(event) => set('title', event.target.value || null)} />
                  </Field>
                  <Field label="Иллюстрация">
                    <Select value={form.art ?? ''} onChange={(event) => set('art', (event.target.value || null) as AnnouncementInput['art'])}>
                      <option value="">Без иллюстрации</option>
                      <option value="copy">Сигналы</option>
                      <option value="markets">Рынки</option>
                      <option value="deposit">Пополнение</option>
                    </Select>
                  </Field>
                </div>
                <div className={styles.grid2}>
                  <Field label="Текст кнопки" hint="Пусто — «Открыть» на языке объявления.">
                    <Input value={form.ctaLabel ?? ''} onChange={(event) => set('ctaLabel', event.target.value || null)} />
                  </Field>
                  <Field label="Куда ведёт" hint="Путь внутри приложения: /signals, /markets, /wallet/deposit…" error={form.ctaTo || form.body.trim() === '' ? undefined : 'Без ссылки баннер не показывается.'}>
                    <Input mono value={form.ctaTo ?? ''} placeholder="/signals" onChange={(event) => set('ctaTo', event.target.value || null)} />
                  </Field>
                </div>
              </>
            ) : null}
            <Field label={form.kind === 'banner' ? 'Подзаголовок' : 'Текст строки'} aside={<span className="num">{form.body.length}</span>}>
              <Textarea value={form.body} onChange={(event) => set('body', event.target.value)} rows={form.kind === 'banner' ? 3 : 2} />
            </Field>
            <div className={styles.grid2}>
              <Field label="Показывать с" hint="Пусто — сразу.">
                <Input type="datetime-local" value={toLocal(form.startsAt)} onChange={(event) => set('startsAt', fromLocal(event.target.value))} />
              </Field>
              <Field label="Показывать до" hint="Пусто — бессрочно.">
                <Input type="datetime-local" value={toLocal(form.endsAt)} onChange={(event) => set('endsAt', fromLocal(event.target.value))} />
              </Field>
            </div>
            <div className={styles.enabledRow}>
              <Toggle switchLast checked={form.isEnabled} label="Включено" description="Выключенное объявление хранится, но не показывается." onChange={(next) => set('isEnabled', next)} />
            </div>
          </div>

          <aside className={styles.previewPane} aria-label="Предпросмотр">
            <div className={styles.previewHead}>
              <span className="eyebrow">Так увидит пользователь</span>
              <PreviewState form={form} />
            </div>
            <div className={styles.phone}>
              {form.kind === 'banner' ? <BannerPreview title={form.title} body={form.body} cta={form.ctaLabel} art={form.art} /> : <TickerPreview text={form.body} />}
            </div>
            <p className={styles.previewNote}>
              <CalendarClock aria-hidden="true" />
              {windowText(form)} · {LOCALE_NAMES[form.locale]}
            </p>
          </aside>
        </div>
      </Modal>
    </>
  )
}

/* ------------------------------------------------------------------------ */
/* parts                                                                    */
/* ------------------------------------------------------------------------ */

function PreviewState({ form }: { form: AnnouncementInput }): JSX.Element {
  const state = stateOf(form)
  return (
    <Badge size="sm" tone={state.tone} pulse={state.live}>
      {state.label}
    </Badge>
  )
}

/** The strip under a preview: state, language, order, window; the switch and the editor. */
function ItemFoot({ row, busy, onToggle, onEdit }: { row: Announcement; busy: boolean; onToggle: (next: boolean) => void; onEdit: () => void }): JSX.Element {
  const state = stateOf(row)
  return (
    <div className={styles.itemFoot}>
      <div className={styles.itemMeta}>
        <Badge size="sm" tone={state.tone} pulse={state.live}>
          {state.label}
        </Badge>
        <span className={styles.metaLocale}>{row.locale.toUpperCase()}</span>
        <span className={styles.metaSep} aria-hidden="true" />
        <span className={styles.metaText} title="Порядок показа">
          <span className="num">#{row.sortOrder}</span>
        </span>
        <span className={styles.metaSep} aria-hidden="true" />
        <span className={styles.metaText} title="Окно показа">
          <CalendarClock aria-hidden="true" />
          {windowText(row)}
        </span>
      </div>
      <div className={styles.itemActions}>
        <Switch checked={row.isEnabled} label="Включено" disabled={busy} onChange={onToggle} />
        <Button size="sm" variant="ghost" icon={<Pencil />} onClick={onEdit}>
          Изменить
        </Button>
      </div>
    </div>
  )
}

/** The app's Banner Card: 148 high, the accent glow in the corner, title, subtitle, the link and the line art. */
function BannerPreview({ title, body, cta, art }: { title: string | null; body: string; cta: string | null; art: AnnouncementInput['art'] }): JSX.Element {
  const kind = artOf(art)
  return (
    <div className={styles.banner} aria-hidden="true">
      <span className={styles.bannerText}>
        <span className={cx(styles.bannerTitle, title ? '' : styles.placeholder)}>{title || 'Заголовок баннера'}</span>
        <span className={cx(styles.bannerBody, body ? '' : styles.placeholder)}>{body || 'Подзаголовок: одна-две строки о том, что внутри.'}</span>
        <span className={styles.bannerLink}>
          {cta || 'Открыть'}
          <ChevronRight />
        </span>
      </span>
      {kind ? <BannerArt kind={kind} /> : null}
    </div>
  )
}

/** The same strokes the app draws: a bar under a broadcast, candles, a coin into a wallet. */
function BannerArt({ kind }: { kind: Art }): JSX.Element {
  const common = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }
  return (
    <svg className={styles.bannerArt} viewBox="0 0 96 72" width="76" height="64" aria-hidden="true">
      {kind === 'signals' ? (
        <>
          <path d="M30 64 L38 46 H74 L82 64 Z" {...common} strokeWidth="2" />
          <path d="M56 46 V30" {...common} opacity="0.7" />
          <circle cx="56" cy="26" r="2.5" fill="currentColor" stroke="none" />
          <path d="M47 17a13 13 0 0 1 18 0" {...common} opacity="0.7" />
          <path d="M40 10a23 23 0 0 1 32 0" {...common} opacity="0.45" />
        </>
      ) : kind === 'markets' ? (
        <path d="M14 14v44M6 26h16v18H6zM40 8v56M32 20h16v26H32zM66 18v46M58 30h16v22H58zM88 6v40M82 14h12v20H82z" {...common} opacity="0.9" />
      ) : (
        <>
          <path d="M12 34h64a6 6 0 0 1 6 6v20a6 6 0 0 1-6 6H18a6 6 0 0 1-6-6z" {...common} />
          <path d="M66 46h16v10H66a5 5 0 0 1 0-10z" {...common} />
          <circle cx="46" cy="16" r="9" {...common} />
          <path d="M46 25v6M42 28l4 4 4-4" {...common} />
        </>
      )}
    </svg>
  )
}

/** The app's ticker row: a 40 px pill, the megaphone, the line, the pause. */
function TickerPreview({ text }: { text: string }): JSX.Element {
  return (
    <div className={styles.ticker} aria-hidden="true">
      <Megaphone className={styles.tickerIcon} />
      <span className={styles.tickerViewport}>
        <span className={cx(styles.tickerText, text ? '' : styles.placeholder)}>{text || 'Текст бегущей строки'}</span>
        <span className={styles.tickerDot} />
      </span>
      <Pause className={styles.tickerIcon} />
    </div>
  )
}

function toLocal(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function fromLocal(value: string): string | null {
  return value === '' ? null : new Date(value).toISOString()
}
